import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getReferenceTableById } from "../../../api/reference";
import { getVariables } from "../../../api/variables";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import {
  ConstructionProfileForm as ConstructionProfileFormType,
  getConstructionProfileFormSchema,
} from "../../../types/forms/capture";
import { ConstructionProfile } from "../../../types/model/constructionProfile";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import FormAutocomplete from "../../General/FormAutocomplete";

interface ConstructionProfileFormProps {
  constructionProfileData?: ConstructionProfile;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: ConstructionProfileFormType) => Promise<void>;
}

export default function ConstructionProfileForm({
  constructionProfileData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<ConstructionProfileFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getConstructionProfileFormSchema(t);
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      startDepth: constructionProfileData?.startDepth ?? undefined,
      endDepth: constructionProfileData?.endDepth ?? undefined,
      variableId: constructionProfileData?.variableId ?? undefined,
      value: constructionProfileData?.value ?? null,
      drainLength: constructionProfileData?.drainLength ?? null,
      notes: constructionProfileData?.notes ?? null,
    },
  });

  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(
          false,
          VARIABLE_CATEGORY_CODES.CONSTRUCTION_PROFILE,
        );
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, []);

  const variableId = methods.watch("variableId");
  const selectedVariable = variables.find((v: any) => v.id === variableId);
  const [lookupTableInfo, setLookupTableInfo] = useState<{
    schemaName: string;
    tableName: string;
  } | null>(null);

  useEffect(() => {
    if (selectedVariable?.isLookup && selectedVariable.lookupTableId) {
      getReferenceTableById(selectedVariable.lookupTableId)
        .then((data) =>
          setLookupTableInfo({
            schemaName: data.schemaName,
            tableName: data.tableName,
          }),
        )
        .catch((error) => {
          handleError(error);
          setLookupTableInfo(null);
        });
    } else {
      setLookupTableInfo(null);
    }
  }, [selectedVariable, methods]);

  const { data: lookupOptions = [] } = useReferenceData(
    lookupTableInfo?.schemaName as string,
    lookupTableInfo?.tableName as string,
  );

  const handleFormSubmit = async (values: ConstructionProfileFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={`${t("captures.capture.construction-profile.start-depth")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("startDepth", { valueAsNumber: true })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.startDepth}
              helperText={methods.formState.errors.startDepth?.message}
            />
          </Grid2>

          <Grid2 size={4}>
            <TextField
              fullWidth
              label={`${t("captures.capture.construction-profile.end-depth")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("endDepth", { valueAsNumber: true })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.endDepth}
              helperText={methods.formState.errors.endDepth?.message}
            />
          </Grid2>

          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.construction-profile.drain-length")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              type="text"
              inputMode="decimal"
              {...methods.register("drainLength")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.drainLength}
              helperText={methods.formState.errors.drainLength?.message}
            />
          </Grid2>

          <Grid2 size={4}>
            <FormAutocomplete
              name="variableId"
              label={t("captures.capture.construction-profile.variable")}
              options={variables}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          {isEditing ? (
            <Grid2 size={4}>
              {selectedVariable?.isLookup ? (
                <FormAutocomplete
                  name="value"
                  label={t("captures.capture.construction-profile.value")}
                  options={lookupOptions}
                  isEditing={isEditing}
                  isSaving={isSaving}
                  disabled={!isEditing || isSaving || isDeleting || !variableId}
                  formStyles={formStyles}
                />
              ) : (
                <TextField
                  fullWidth
                  label={`${t("captures.capture.construction-profile.value")} *`}                       
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing || isSaving || isDeleting || !variableId}
                  sx={{ ...formStyles }}
                  {...methods.register(
                    "value",
                    { valueAsNumber: true }
                  )}
                  error={!!methods.formState.errors.value}
                  helperText={methods.formState.errors.value?.message}
                />
              )}
            </Grid2>
          ) : (
            <Grid2 size={4}>
              <TextField
                label={`${t("captures.capture.construction-profile.value")} *`}                       
                variant="standard"
                disabled 
                sx={{ 
                  ...formStyles, 
                  width: `${Math.max(40, (String(methods.getValues("value")).length + (selectedVariable?.unit?.length ?? 0) + 1) * 10)}px`
                }}
                {...methods.register("value", { valueAsNumber: true })}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{selectedVariable?.unit}</InputAdornment>
                  },
                  inputLabel: { shrink: true }
                }}
              />
            </Grid2>
          )}
  
          <Grid2 size={12}>
            <TextField
              fullWidth
              label={t("captures.capture.construction-profile.notes")}
              placeholder={t(
                "captures.capture.construction-profile.notes-placeholder",
              )}
              multiline
              minRows={6}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("notes")}
              error={!!methods.formState.errors.notes}
              helperText={methods.formState.errors.notes?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
